import type { DateTimeFieldProps } from './date-time-field.types';
import { getCurrentTime } from '@/lib/dev-clock';

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

// `datetime-local` inputs expect a local "YYYY-MM-DDTHH:mm" string.
function toInputValue(date: Date | null, mode: 'date' | 'time' | 'datetime'): string {
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }
  const datePart = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const timePart = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  if (mode === 'date') return datePart;
  if (mode === 'time') return timePart;
  return `${datePart}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function DateTimeField({
  error,
  value,
  onChange,
  minimumDate,
  mode = 'datetime',
  label = 'Date',
}: DateTimeFieldProps) {
  return (
    <div>
      <div
        style={{
          alignItems: 'center',
          borderBottom: '1px solid var(--border, #e5e7eb)',
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: 56,
        }}
      >
        <span>{label}</span>
        <input
          aria-label={label}
          aria-describedby={error ? `${label}-error` : undefined}
          aria-invalid={Boolean(error)}
          type={mode === 'datetime' ? 'datetime-local' : mode}
          value={toInputValue(value, mode)}
          min={minimumDate ? toInputValue(minimumDate, mode) : undefined}
          onChange={(event) => {
            const next = event.target.value;
            if (next) {
              if (mode === 'date') {
                const [year, month, day] = next.split('-').map(Number);
                const selected = new Date(value ?? getCurrentTime());
                selected.setFullYear(year, month - 1, day);
                onChange(selected);
              } else if (mode === 'time') {
                const [hour, minute] = next.split(':').map(Number);
                const selected = new Date(value ?? getCurrentTime());
                selected.setHours(hour, minute, 0, 0);
                onChange(selected);
              } else {
                onChange(new Date(next));
              }
            }
          }}
          style={{
            backgroundColor: 'transparent',
            border: 0,
            color: 'inherit',
            fontFamily: 'inherit',
            fontSize: 14,
            minHeight: 44,
          }}
        />
      </div>
      {error ? (
        <p id={`${label}-error`} style={{ color: 'hsl(var(--destructive))', margin: '4px 0 0' }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
